/**
 * @file useSystemStatus.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-18
 * @modified 2026-10-03
 * @description Polls the maintenance lock and the server's build id, and turns
 *              them into the one thing the UI needs: which phase we are in.
 *              The same reply carries Xero's health (ok / degraded / down /
 *              auth_problem), returned as `xeroHealth` for XeroHealthBanner.
 *
 *                idle     nothing happening
 *                warning  a lock is scheduled — countdown, finish your work
 *                locked   saving is blocked right now
 *                stale    lock lifted and the bundle changed — needs a refresh
 *
 *              The countdown runs off the server clock, not the browser's. An
 *              office PC minutes out of sync would otherwise show the wrong time
 *              remaining, or none at all.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import { publishXeroLock } from './useXeroLock';

const POLL_IDLE_MS = 20000;
// Once something is scheduled, poll hard: the difference between "saving closes
// in 15s" and "…in 35s" is the difference between finishing an order and losing it.
const POLL_ACTIVE_MS = 3000;

export default function useSystemStatus({ enabled = true } = {}) {
  const [lock, setLock] = useState(null);
  // Is Xero answering? Kept as last reported: a failed poll keeps it, so a
  // blip in OUR server never paints Xero as down (or as fine).
  const [xeroHealth, setXeroHealth] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [secondsUntilLock, setSecondsUntilLock] = useState(null);

  // The build the tab loaded against. Captured from the first successful poll
  // rather than baked in at build time, so it needs no webpack plumbing.
  const originalBuild = useRef(null);
  const sawLock = useRef(false);
  const skewMs = useRef(0);
  const [buildChanged, setBuildChanged] = useState(false);

  const poll = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance-lock`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();

      if (data.serverTime) skewMs.current = new Date(data.serverTime).getTime() - Date.now();
      if (data.buildId) {
        if (originalBuild.current === null) originalBuild.current = data.buildId;
        else if (data.buildId !== originalBuild.current) setBuildChanged(true);
      }
      setLock(data.lock || null);
      // The same reply carries Xero's accounting lock: keeps every tab's lock
      // icon (and date pickers) current when someone else refreshes it.
      if (data.xeroLock) publishXeroLock(data.xeroLock);
      setXeroHealth(data.xeroHealth || null);
      if (data.lock?.is_active) sawLock.current = true;
    } catch (_) {
      // A failed poll is not itself a signal — the server may simply be
      // restarting mid-deploy, which is precisely when we must not flap the UI.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    let timer = null;
    const schedule = () => {
      const active = phase === 'warning' || phase === 'locked';
      timer = setTimeout(async () => { await poll(); schedule(); },
        active ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    schedule();
    return () => { if (timer) clearTimeout(timer); };
  }, [enabled, poll, phase]);

  // Derive phase and countdown on a 1s tick so the number moves smoothly
  // between polls rather than jumping in 3s steps.
  useEffect(() => {
    const tick = () => {
      const now = Date.now() + skewMs.current;

      if (lock?.is_active) {
        setPhase('locked');
        setSecondsUntilLock(0);
        return;
      }
      if (lock?.starts_at) {
        const secs = Math.max(0, Math.round((new Date(lock.starts_at).getTime() - now) / 1000));
        setPhase('warning');
        setSecondsUntilLock(secs);
        return;
      }
      // Lock has lifted. If the bundle moved while we waited, this tab is
      // running code the server no longer serves.
      if (sawLock.current && buildChanged) {
        setPhase('stale');
        setSecondsUntilLock(null);
        return;
      }
      if (sawLock.current) sawLock.current = false;
      setPhase('idle');
      setSecondsUntilLock(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lock, buildChanged]);

  return { lock, phase, secondsUntilLock, buildChanged, xeroHealth, refresh: poll };
}
